import { mfaB2BRegister } from '@ytl/common-model';
import { Button, CodeInput, CodeInputRef, useTheme } from '@ytl/common-web';
import axios from 'axios';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/languageContext';
import { useOpenAm } from '../contexts/openAmContext';
import { OpenAmAuthenticateError, OpenAmAuthError, StageFailure } from '../shared/@types/OpenAmInitialData';
import { LoginView, StageFailureType } from '../shared/@types/OpenAmStage';
import { useOpenAmStage } from '../shared/services/useOpenAmStage';

const RESEND_COOLDOWN_SECONDS = 30;

export const MSISDNVerificatorCodeStage = () => {
  const { translation } = useLanguage();
  const openam = useOpenAm();
  //const { isLoading, previousStage, initParams } = useOpenAm();
  const [smsCode, setSmsCode] = useState('');
  const [error, setError] = useState('');
  const { loginWithMSISDNVerificatorCode, reSendMSISDNVerificatorCode } = useOpenAmStage();
  const { isWebView } = useTheme();
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const codeInputRef = useRef<CodeInputRef>(null);
  const reSendMsisdnCodeForcedRef = useRef<() => Promise<void>>();

  const blocked = useCallback(() => {
    openam.setError(OpenAmAuthError.Blocked);
  }, [openam]);

  useEffect(() => {
    try {
      if (openam.stage.failure?.stagefailure.startsWith(StageFailureType.Blocked)) {
        blocked();
        return;
      }
      const parsedInfoText = JSON.parse(openam.stage.infoText?.at(0) || '{}');
      console.log(parsedInfoText);
      const cooldown = Number(parsedInfoText.cooldownSeconds);
      setResendTimer(isNaN(cooldown) ? RESEND_COOLDOWN_SECONDS : cooldown);
    } catch (e) {
      console.error('cannot parse infoText', e);
      setResendTimer(RESEND_COOLDOWN_SECONDS);
    }
  }, [setResendTimer, openam.stage.infoText, blocked, openam.stage.failure]);

  const prevIsWebViewRef: MutableRefObject<any> = useRef(null);
  useEffect(() => {
    if (prevIsWebViewRef.current !== isWebView) {
      try {
        const param = (url: URL, param: string): URL => {
          return new URL(url.searchParams.has(param) ? (url.searchParams.get(param) as string) : url.toString());
        };
        const url = new URL(window.location.href);
        const goto = param(url, 'goto'); // new URL(url.searchParams.has('goto') ? (url.searchParams.get('goto') as string) : url.toString());
        console.log(goto);
        const redirect_uri = param(goto, 'redirect_uri'); //new URL(goto.searchParams.has('redirect_uri') ? (goto.searchParams.get('redirect_uri') as string) : goto.toString());
        console.log(redirect_uri);
        if (redirect_uri.pathname.endsWith(mfaB2BRegister)) {
          prevIsWebViewRef.current = isWebView;
        }
      } catch (e) {
        console.warn('cannot get redirect_uri', e);
      }
    }
  }, [isWebView]);
  useEffect(() => {
    if (!openam.isLoading) {
      codeInputRef.current?.focusFirst();
    }
  }, [openam.isLoading]);

  const _action = useCallback(
    async (
      action: (c: string, failureHandler: (res: StageFailure, mfa: boolean) => void) => Promise<void>,
      code: string,
    ) => {
      try {
        openam.setIsLoading(true);
        await action(code, failure => {
          if (failure.stagefailure.startsWith(StageFailureType.MsisdnInvalidCode)) {
            const remaining = failure.stagefailure.split(':')[1] ?? '1';
            const codeSent = code;
            codeInputRef.current?.clear();
            setError(translation.loginRegister.login.msisdnverification.invalidCode(codeSent, remaining));
          } else if (failure.stagefailure.startsWith(StageFailureType.MsisdnRetryLimit)) {
            openam.setError({
              type: OpenAmAuthError.MSISDNRetryLimit,
              callback: () => {
                openam.setError(undefined);
                openam.setStage(LoginView.MSISDNCode, openam.stage.infoText ?? [], LoginView.MSISDNCode);
                reSendMsisdnCodeForcedRef.current?.();
              },
            });
            // } else if (failure.stagefailure.startsWith(StageFailureType.Blocked)) {
            //   debugger;
            //   blocked();
            //   return;
          } else if (
            failure.stagefailure.startsWith(StageFailureType.MsisdnResendLimit) ||
            failure.stagefailure.startsWith(StageFailureType.Blocked)
          ) {
            openam.setError(OpenAmAuthError.MSISDNResendLimit);
            return;
          } else {
            setError(translation.loginRegister.login.box.loginError.general);
          }
        });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const axiosError = err as OpenAmAuthenticateError;
          if (axiosError.response?.data?.message?.includes(StageFailureType.MsisdnResendLimit)) {
            openam.setError(OpenAmAuthError.MSISDNResendLimit);
            return;
          } else if (axiosError.response?.data?.message?.includes(StageFailureType.Blocked)) {
            blocked();
            return;
          } else {
          }
        } else {
        }
        setError(translation.loginRegister.login.box.loginError.general);
      } finally {
        openam.setIsLoading(false);
      }
    },
    [setError, translation, openam, blocked],
  );

  const sendMsisdnCode = useCallback(
    async (code: string) => {
      await _action(loginWithMSISDNVerificatorCode, code);
    },
    [_action, loginWithMSISDNVerificatorCode],
  );

  const reSendMsisdnCode = useCallback(async () => {
    if (resendTimer > 0) return;

    await _action(reSendMSISDNVerificatorCode, '');
    setResendTimer(RESEND_COOLDOWN_SECONDS);
  }, [_action, reSendMSISDNVerificatorCode, resendTimer]);

  reSendMsisdnCodeForcedRef.current = useCallback(async () => {
    await _action(reSendMSISDNVerificatorCode, '');
    setResendTimer(RESEND_COOLDOWN_SECONDS);
  }, [_action, reSendMSISDNVerificatorCode]);

  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);
  return (
    <>
      <h3>{translation.loginRegister.login.msisdnverification.header}</h3>
      <div className="login-page__container__verification-step__sub-header">
        {translation.loginRegister.login.msisdnverification.subHeader}
      </div>
      <div className={'login-page__sms-code-input-container'}>
        <CodeInput
          ref={codeInputRef}
          value={smsCode}
          onChange={v => {
            setSmsCode(v);
            setError('');
          }}
          onComplete={sendMsisdnCode}
          hasError={!!error}
          disabled={openam.isLoading}
          autoFocus={true}
        />
      </div>

      {error && <div className="login-page__container__error">{error}</div>}
      <div className="login-page__container__verification-step__resend">
        <Button
          className="login-page__container__verification-step__resend__button"
          variant={'text-undecorated'}
          onClick={reSendMsisdnCode}
          disabled={openam.isLoading || resendTimer > 0}
        >
          {translation.loginRegister.login.msisdnverification.action}
        </Button>
        {resendTimer > 0 && (
          <span className="login-page__container__verification-step__resend__timer">
            ({resendTimer} {translation.loginRegister.login.msisdnverification.timerSeconds})
          </span>
        )}
      </div>
    </>
  );
};
